/**
 * Toast — Floating notification bar (success / error / info)
 *
 * Usage:
 *   <Toast type="success" message="Items added to cart" onClose={() => setToast(null)} />
 *
 * Auto-dismisses after 4 seconds. Can be closed manually via X button.
 */
import { useEffect, useState } from 'react';

const TYPE_CONFIG = {
  success: {
    icon: 'check_circle',
    bg: 'bg-success',
    text: 'text-white',
  },
  error: {
    icon: 'error',
    bg: 'bg-red-600',
    text: 'text-white',
  },
  info: {
    icon: 'info',
    bg: 'bg-electric-blue',
    text: 'text-white',
  },
  warning: {
    icon: 'warning',
    bg: 'bg-warning',
    text: 'text-carbon-black',
  },
};

export default function Toast({ type = 'info', message, onClose, duration = 4000 }) {
  const [visible, setVisible] = useState(true);
  const config = TYPE_CONFIG[type] || TYPE_CONFIG.info;

  useEffect(() => {
    if (!duration) return;
    const timer = setTimeout(() => {
      setVisible(false);
      setTimeout(() => onClose?.(), 300);
    }, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  return (
    <div
      className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-5 py-3 shadow-lg
        ${config.bg} ${config.text}
        transition-all duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
      role="alert"
    >
      <span className="material-symbols-outlined text-xl">{config.icon}</span>
      <span className="text-sm font-sandvik-text">{message}</span>
      <button
        onClick={() => { setVisible(false); setTimeout(() => onClose?.(), 300); }}
        className="ml-2 opacity-80 hover:opacity-100"
        aria-label="Dismiss"
      >
        <span className="material-symbols-outlined text-lg">close</span>
      </button>
    </div>
  );
}
